import { ReactNode } from 'react';
import {
  COUNTRY,
  ERROR_MESSAGE,
  pmsDropDownList,
  US_STATE
} from '../utils/constantTypes';

export enum API_RESPONSE {
  SESSION_NOT_FOUND = 'No session found!'
}

export enum ROLES {
  ADMIN = 'Admin',
  DENTIST = 'Dentist',
  DOCTOR_ASSISTANT = 'Dental Assistant',
  TRIAL_USER = 'Trial User'
}

export const SELECTED_USER_DIALOG_BOX_FIELDS = [
  'firstName',
  'lastName',
  'fullName',
  'email',
  'role',
  'agreementAccepted',
  'licenseState',
  'managementSoftware'
];

export const APPOINTMENT_STATUS_COLOR = {
  start: '#89E141',
  paused: '#EEA82E',
  complete: '#3E4FEC',
  submitted: '#c92626',
  inprogress: '#a9a9a9'
};

export enum SIGNUP_PROCESS_STEPS {
  SEND_EMAIL_OTP = 'SEND_EMAIL_OTP',
  VERIFY_NEW_USER = 'VERIFY_NEW_USER',
  VERIFY_EMAIL_OTP = 'VERIFY_EMAIL_OTP',
  RESEND_EMAIL_OTP = 'RESEND_EMAIL_OTP',
  VERIFY_MOBILE_OTP = 'VERIFY_MOBILE_OTP',
  RESEND_MOBILE_OTP = 'RESEND_MOBILE_OTP'
}

export enum ERRORS {
  INVALID_PMS = 'Invalid PMS selected',
  INVALID_MOBILE_NUM = 'Wrong phone number',
  INVALID_SECONDARY_MOBILE_NUM = 'Wrong secondary phone number',
  EXISTING_EMAIL = 'If you already have an account, please try signing in or use a different email for signup.'
}

export const AUTH_ERROR_CODES = {
  PRACTICE_INCOMPLETE: 'PRACTICE_INCOMPLETE'
} as const;

export const REQ_TOKENS_KEY_NAMES = ['accessToken', 'refreshToken'];
export const IS_OTP_VERIFIED = ['isOtpVerified'];

export const REQ_LOCAL_KEYS = ['role'];

export const DATE_FORMATS = {
  MONTH_FIRST: 'MM/DD/YYYY',
  MONTH_FIRST_AND_TIME: 'MM/DD/YYYY hh:mm A'
};

export enum FORM_TYPES {
  FORGOT_PASSWORD = 'FORGOT_PASSWORD',
  RESET_PASSWORD = 'RESET_PASSWORD'
}

export interface ForgotPasswordFormFieldType {
  email: string;
}

export interface ResetPasswordFormFieldType {
  password: string;
  confirmPassword: string;
}
export interface FormFieldsType {
  labelImageName: string;
  label: string;
  placeholder: string;
  id: string;
  value: string;
  validateError: boolean;
  validateErrormsg: string;
  onChangeText: any; //TODO: () => void
  icon: ReactNode;
  type: string;
  iconName?: string;
  passwordVisible?: boolean;
  onIconClick?: () => void;
  inputProps?: boolean;
}

export interface BillingDetailsFromFieldType {
  addLine1: string;
  addLine2: string;
  suite: string;
  city: string;
  state: string;
  cardHolderName: string;
  country: string;
  zipcode: string;
}

export const SUBSCRIPTION_TYPE = {
  FREE: 'Free (30 days trial)',
  STANDARD: 'Standard ($699/Month)',
  PREMIUM: 'Premium'
};

export const STRIPE_CARD_ELMS = {
  CARD_NUMBER: 'cardNumber',
  CARD_EXPIRY: 'cardExpiry',
  CARD_CVC: 'cardCvc'
};

export const STRIPE_CARD_ELM_FIELDS = {
  COMPLETE: 'complete',
  ERROR: 'error',
  EMPTY: 'empty'
};

export const STRIPE_CARD_ELMS_IDS = {
  [STRIPE_CARD_ELMS.CARD_NUMBER]: 'card-number',
  [STRIPE_CARD_ELMS.CARD_EXPIRY]: 'card-expiry',
  [STRIPE_CARD_ELMS.CARD_CVC]: 'card-cvc'
};

export const STRIPE_CARD_ELMS_ERR_MSGS = {
  [STRIPE_CARD_ELMS.CARD_NUMBER]: {
    INVALID: 'Invalid card number.',
    REQUIRED: 'Card number is required.'
  },
  [STRIPE_CARD_ELMS.CARD_EXPIRY]: {
    INVALID: 'Invalid card expiry date.',
    REQUIRED: 'Card expiry date is required.'
  },
  [STRIPE_CARD_ELMS.CARD_CVC]: {
    INVALID: 'Invalid card cvc.',
    REQUIRED: 'Card cvc is required.'
  }
};

export enum SLICES {
  AUTH = 'auth',
  USER = 'user',
  SUBSCRIPTION = 'SUBSCRIPTION',
  DASHBOARD = 'DASHBOARD',
  REPORT = 'REPORT'
}

export enum ACTION_STATE {
  PENDING = 'pending',
  FULFILLED = 'fulfilled',
  REJECTED = 'rejected'
}

export enum ACTION_NAMES {
  LOGIN = 'loginAsync',
  CREATE_TRIAL_USER = 'createTrailUserAsync',
  CREATE_ROLE = 'createRoleAsync',
  DELETE_TRIAL_USER = 'deleteTrialUserAsync',
  UPDATE_TRIAL_USER = 'updateTrialUserAsync',
  VALIDATE_DISCOUNT_CODE = 'validateDiscountCode',
  GET_INVOICES_BY_CUST_ID = 'getInvoicesByCustomerIdAsync',
  GET_NEXT_INVOICE_DATE_BY_CUST_ID = 'getNextInvoiceDateByCustomerIdAsync',
  UPDATE_EMAIL_AND_PHONE_NUMBER = 'updatEmailAndPhoneNumberAsync',
  DELETE_ALL_ACTIVE_SESSION = 'deleteUsersAllActiveSessionsAsync',
  SEND_OTP = 'sendOtpAsync',
  VERIFY_OTP = 'verifyOtpAsync',
  UPDATE_PRACTICE_DETAILS = 'updatePracticeDetailsAsync',
  FETCH_PRACTICE_DETAILS_BY_ID = 'fetchPracticeDetailsById',
  SEND_SPU_DATA_REFRESH_STATUS_EMAIL = 'sendSPUDataRefreshStatusEmailAsync',
  GET_REPORTS_BY_FILTERS = 'getReportsByFiltersAsync',
  DELETE_TRIAL_USERS_REPORTS = 'deleteTrailUsersReportsAsync',
  IMPORT_CDT_CODES_PLAN = 'importCDTCodesPlanAsync',
  FETCH_USER_DETAILS_BY_ID = 'fetchUserDetailsByIdAsync',
  IMPORT_INTERNAL_PLANS = 'importInternalPlansAsync',
  DELETE_USERS_BY_IDS = 'deleteUsersByIdsAsync',
  GET_REPORT_WRITEBACK_FAILURE_ANALYSIS = 'getReportWritebackFailureAnalysisAsync',
  ENABLE_PRACTICE_LOCATION = 'enablePracticeLocationAsync',
  TOGGLE_IS_SHEET_LOCK = 'toggleIsSheetLockAsync'
}

export enum ReportFileType {
  FINAL_REPORT = 'FINAL_REPORT',
  ANALYSIS = 'ANALYSIS',
  PERIO_CHART = 'PERIO_CHART',
  PERIO_TRANSCRIPT = 'PERIO_TRANSCRIPT'
}

export const ACTIONS = {
  [SLICES.AUTH]: {
    [ACTION_NAMES.LOGIN]: {
      [ACTION_STATE.REJECTED]: `${SLICES.AUTH}/${ACTION_NAMES.LOGIN}/${ACTION_STATE.REJECTED}`
    }
  },
  [SLICES.USER]: {
    [ACTION_NAMES.CREATE_TRIAL_USER]: {
      [ACTION_STATE.PENDING]: `${SLICES.USER}/${ACTION_NAMES.CREATE_TRIAL_USER}/${ACTION_STATE.PENDING}`,
      [ACTION_STATE.FULFILLED]: `${SLICES.USER}/${ACTION_NAMES.CREATE_TRIAL_USER}/${ACTION_STATE.FULFILLED}`,
      [ACTION_STATE.REJECTED]: `${SLICES.USER}/${ACTION_NAMES.CREATE_TRIAL_USER}/${ACTION_STATE.REJECTED}`
    }
  }
};

export enum DISCOUNT_TYPE {
  AMOUNT_OFF = 'AMOUNT_OFF',
  PERCENT_OFF = 'PERCENT_OFF'
}

export enum DISCOUNT_DURATION {
  ONCE = 'once',
  FOREVER = 'forever',
  REPEAT = 'repeating'
}

export enum PROFILE_STEPS {
  DS_REG = 'DS_REG',
  PRACTICE_REG = 'PRACTICE_REG',
  SIKKA_REG = 'SIKKA_REG',
  PRACTICE_LOCATION = 'PRACTICE_LOCATION',
  SUBSCRIPTION_PURCHASE = 'SUBSCRIPTION_PURCHASE',
  COMPLETE = 'COMPLETE'
}

export enum INVOICES_FREQUENCY {
  month = 'Monthly'
}

export const TOAST_MSGS = {
  UPDATE_EMAIL: 'Email updated successfully. Please login again.',
  UPDATE_PHN_NUM: 'Phone number updated successfully. Please login again.',
  TOGGLE_SHEET_IS_LOCK: 'An error occurred while updating sheet.'
};

export enum MODAL_TYPES {
  EXTEND_USER_SUBSCRIPTION = 'EXTEND_USER_SUBSCRIPTION',
  PAUSE_SUBSCRIPTION = 'PAUSE_SUBSCRIPTION',
  RESUME_SUBSCRIPTION = 'RESUME_SUBSCRIPTION',
  DEACTIVATE_ACCOUNT = 'DEACTIVATE_ACCOUNT',
  REACTIVATE_ACCOUNT = 'REACTIVATE_ACCOUNT',
  SEND_PRACTICE_DATA_REFRESH_MAIL = 'SEND_PRACTICE_DATA_REFRESH_MAIL',
  IMPORT_CDT_CODES = 'IMPORT_CDT_CODES',
  SAVE_CDT_PLANS = 'SAVE_CDT_PLANS',
  IMPORT_INTERNAL_CODES = 'IMPORT_INTERNAL_CODES',
  SAVE_INTERNAL_PLANS = 'SAVE_INTERNAL_PLANS',
  DELETE_USER = 'DELETE_USER',
  SPU_LINK = 'SPU_LINK'
}

export enum EMAIL_TYPES {
  SEND_SPU_DOWN_MAIL = 'SEND_SPU_DOWN_MAIL'
}

export enum DB_REPORTS_STATUS_MAPPING {
  start = 'START',
  submitted = 'REVIEWED',
  complete = 'REVIEW',
  paused = 'CONTINUE',
  inprogress = 'INPROGRESS',
  null = 'NA'
}

export enum DROPDOWN_ENUM {
  ROLE = 'role',
  STATE = 'licenseState',
  PMS = 'pms',
  COUNTRY = 'country'
}

export const DROPDOWN_LIST_OBJ = {
  [DROPDOWN_ENUM.ROLE]: [
    { key: 'Dentist', value: 'Dentist' },
    { key: 'Dental Assistant', value: 'Dental Assistant' }
  ],
  [DROPDOWN_ENUM.STATE]: JSON.parse(JSON.stringify(US_STATE)),
  [DROPDOWN_ENUM.PMS]: JSON.parse(JSON.stringify(pmsDropDownList)),
  [DROPDOWN_ENUM.COUNTRY]: COUNTRY
};

export const ERR_MSG = {
  city: ERROR_MESSAGE.cityRequired,
  addressLine1: ERROR_MESSAGE.addressOneRequired,
  zipcode: ERROR_MESSAGE.zipCodeRequired
};

export enum SHEET_TYPES {
  CDT_CODES = 'cdtCode',
  INTERNAL_CODES = 'internalCode'
}
